import {
  Database,
  Eye,
  EyeOff,
  ChevronRight,
  Image,
  LayoutList,
  Layers,
  Square,
  Star,
  Trash2,
  Type,
} from "lucide-react";
import type React from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { CollectionSourceDialog } from "./CollectionSourceDialog";

const DropdownIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <polyline points="8 11 12 15 16 11" />
  </svg>
);

interface SkinComponent {
  id: string;
  type: "box" | "text" | "image" | "icon" | "collection-list" | "tabs" | "dropdown";
  name: string;
  position: {
    left?: number | string;
    right?: number | string;
    top?: number | string;
    bottom?: number | string;
  };
  size: { width: number | string; height: number | string; aspectRatio?: string };
  style: Record<string, unknown>;
  content: Record<string, unknown>;
  visible: boolean;
  parentId?: string;
  zIndex: number;
  pointerEvents?: boolean;
  hideOnStart?: boolean;
}

interface ComponentLibraryProps {
  components: SkinComponent[];
  selectedComponent: SkinComponent | null;
  onComponentSelect: (component: SkinComponent | null) => void;
  onCreateComponent: (
    type: SkinComponent["type"],
    additionalData?: Record<string, unknown>,
  ) => void;
  onDeleteComponent: (componentId: string) => void;
  onReorderComponents: (components: SkinComponent[]) => void;
  onNestComponent: (childId: string, parentId: string | null) => void;
  onUpdateComponent: (componentId: string, updates: Partial<SkinComponent>) => void;
}

export const ComponentLibrary = ({
  components,
  selectedComponent,
  onComponentSelect,
  onCreateComponent,
  onDeleteComponent,
  onReorderComponents,
  onNestComponent,
  onUpdateComponent,
}: ComponentLibraryProps) => {
  const { toast } = useToast();
  const [draggedComponent, setDraggedComponent] = useState<string | null>(null);
  const [dragOverComponent, setDragOverComponent] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<"before" | "inside" | "after" | null>(
    null,
  );
  const [editingComponentId, setEditingComponentId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [collectionSourceDialogOpen, setCollectionSourceDialogOpen] = useState<boolean>(false);
  const [expandedComponents, setExpandedComponents] = useState<Record<string, boolean>>({});
  const componentRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const previousRowRects = useRef<Map<string, DOMRect>>(new Map());

  // Sort components by zIndex (higher zIndex = higher in list = on top)
  const sortedComponents = useMemo(
    () => [...components].sort((a, b) => b.zIndex - a.zIndex),
    [components],
  );
  const layoutAnimationKey = useMemo(
    () =>
      sortedComponents
        .map((component) => `${component.id}:${component.parentId ?? "root"}:${component.zIndex}`)
        .join("|"),
    [sortedComponents],
  );

  // Group components by parent
  const rootComponents = sortedComponents.filter((c) => !c.parentId);
  const getChildComponents = (parentId: string) =>
    sortedComponents.filter((c) => c.parentId === parentId).sort((a, b) => b.zIndex - a.zIndex);

  useLayoutEffect(() => {
    if (layoutAnimationKey.length === 0) {
      previousRowRects.current = new Map();
      return;
    }

    const currentRects = new Map<string, DOMRect>();
    componentRowRefs.current.forEach((row, id) => {
      if (row.isConnected) {
        currentRects.set(id, row.getBoundingClientRect());
      }
    });

    if (previousRowRects.current.size === 0) {
      previousRowRects.current = currentRects;
      return;
    }

    currentRects.forEach((nextRect, id) => {
      if (id === draggedComponent) return;
      const previousRect = previousRowRects.current.get(id);
      const row = componentRowRefs.current.get(id);
      if (!previousRect || !row) return;

      const deltaX = previousRect.left - nextRect.left;
      const deltaY = previousRect.top - nextRect.top;
      if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;

      row.style.transition = "none";
      row.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      row.style.willChange = "transform";

      requestAnimationFrame(() => {
        row.style.transition = "transform 180ms cubic-bezier(0.22, 1, 0.36, 1)";
        row.style.transform = "translate(0, 0)";
      });

      const clear = () => {
        row.style.transition = "";
        row.style.transform = "";
        row.style.willChange = "";
        row.removeEventListener("transitionend", clear);
      };
      row.addEventListener("transitionend", clear);
    });

    previousRowRects.current = currentRects;
  }, [layoutAnimationKey, draggedComponent]);

  const handleDragStart = (e: React.DragEvent, componentId: string) => {
    setDraggedComponent(componentId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-skin-component-id", componentId);
  };

  const getDropPosition = (e: React.DragEvent, componentId: string) => {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const threshold = rect.height * 0.25;
    const rawPosition = y < threshold ? "before" : y > rect.height - threshold ? "after" : "inside";
    const targetComp = components.find((c) => c.id === componentId);
    const canNest =
      targetComp && (targetComp.type === "box" || targetComp.type === "collection-list");
    if (rawPosition === "inside" && !canNest) {
      return "after";
    }
    return rawPosition;
  };

  const normalizeDropTarget = (
    targetComponentId: string,
    rawPosition: "before" | "inside" | "after",
  ) => {
    if (rawPosition !== "after") {
      return { targetComponentId, position: rawPosition };
    }

    const targetComp = components.find((c) => c.id === targetComponentId);
    if (!targetComp) {
      return { targetComponentId, position: rawPosition };
    }

    const parentId = targetComp.parentId ?? null;
    const siblings = components
      .filter((c) => (c.parentId ?? null) === parentId && c.id !== draggedComponent)
      .sort((a, b) => b.zIndex - a.zIndex);

    const targetIndex = siblings.findIndex((c) => c.id === targetComponentId);
    const nextSibling = targetIndex >= 0 ? siblings[targetIndex + 1] : undefined;

    // Normalize "after current" into "before next" so the divider stays fixed between rows.
    if (nextSibling) {
      return { targetComponentId: nextSibling.id, position: "before" as const };
    }

    return { targetComponentId, position: rawPosition };
  };

  const handleDragOver = (e: React.DragEvent, componentId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rawPosition = getDropPosition(e, componentId);
    const normalized = normalizeDropTarget(componentId, rawPosition);
    setDragOverComponent(normalized.targetComponentId);
    setDragOverPosition(normalized.position);
  };

  const handleDrop = (e: React.DragEvent, targetComponentId: string) => {
    e.preventDefault();
    if (!draggedComponent || draggedComponent === targetComponentId) return;

    const draggedComp = components.find((c) => c.id === draggedComponent);
    const rawPosition = getDropPosition(e, targetComponentId);
    const normalized = normalizeDropTarget(targetComponentId, rawPosition);
    const resolvedTargetId = normalized.targetComponentId;
    const dropPosition = normalized.position;

    if (draggedComponent === resolvedTargetId) return;

    const targetComp = components.find((c) => c.id === resolvedTargetId);

    if (!draggedComp || !targetComp) return;
    const isContainer =
      targetComp.type === "box" ||
      targetComp.type === "collection-list" ||
      targetComp.type === "tabs";

    if (dropPosition === "inside" && isContainer) {
      onNestComponent(draggedComponent, resolvedTargetId);
    } else {
      const nextParentId = targetComp.parentId ?? null;
      const siblings = components
        .filter((c) => (c.parentId ?? null) === nextParentId && c.id !== draggedComponent)
        .sort((a, b) => b.zIndex - a.zIndex);
      const targetIndex = siblings.findIndex((c) => c.id === resolvedTargetId);
      const insertIndex = dropPosition === "before" ? targetIndex : targetIndex + 1;
      const reordered = [...siblings];
      reordered.splice(insertIndex, 0, { ...draggedComp, parentId: nextParentId });
      const updatedComponents = components.map((c) => {
        const idx = reordered.findIndex((item) => item.id === c.id);
        if (idx === -1) return c;
        return {
          ...c,
          parentId: nextParentId ?? undefined,
          zIndex: reordered.length - idx,
        };
      });
      onReorderComponents(updatedComponents);
    }

    setDraggedComponent(null);
    setDragOverComponent(null);
    setDragOverPosition(null);
  };

  const handleDragEnd = () => {
    setDraggedComponent(null);
    setDragOverComponent(null);
    setDragOverPosition(null);
  };

  const clearDropIndicator = () => {
    setDragOverComponent(null);
    setDragOverPosition(null);
  };

  const handleTreeDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const isOverRow = !!target.closest("[data-component-row='true']");
    if (!isOverRow) {
      clearDropIndicator();
    }
  };

  const handleTreeDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const isOutsideBounds =
      e.clientX < bounds.left ||
      e.clientX > bounds.right ||
      e.clientY < bounds.top ||
      e.clientY > bounds.bottom;
    if (isOutsideBounds) {
      clearDropIndicator();
    }
  };

  // Handle drag start for component templates (new components)
  const handleTemplateDragStart = (e: React.DragEvent, componentType: string) => {
    e.dataTransfer.effectAllowed = "copy";

    // Set drag data for creating new components
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        type: "component-template",
        componentType: componentType,
        position: { left: 50, top: 50 }, // Default position
        zIndex: Math.max(...components.map((c) => c.zIndex), 0) + 1, // Place on top
      }),
    );

    // Add visual feedback
    const target = e.target as HTMLElement;
    target.style.opacity = "0.5";
  };

  // Handle drag end for component templates
  const handleTemplateDragEnd = (e: React.DragEvent) => {
    // Reset visual feedback
    const target = e.target as HTMLElement;
    target.style.opacity = "1";
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-stop-propagation]")) return;
    // Always deselect when clicking on the container
    onComponentSelect(null);
  };

  const handleComponentTreeClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-stop-propagation]")) return;
    // Always deselect when clicking on empty space in the component tree
    onComponentSelect(null);
  };

  const handleDoubleClick = (component: SkinComponent) => {
    setEditingComponentId(component.id);
    setEditingName(component.name);
  };

  const handleRenameSubmit = () => {
    if (editingComponentId && editingName.trim()) {
      onUpdateComponent(editingComponentId, { name: editingName.trim() });
    }
    setEditingComponentId(null);
    setEditingName("");
  };

  const handleRenameCancel = () => {
    setEditingComponentId(null);
    setEditingName("");
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      handleRenameSubmit();
    } else if (e.key === "Escape") {
      handleRenameCancel();
    }
  };

  const handleCollectionSourceSelected = (sourceId: string) => {
    // Create the collection list component with the selected source
    if (selectedComponent?.type === "box" || selectedComponent?.type === "collection-list") {
      setExpandedComponents((prev) => ({ ...prev, [selectedComponent.id]: true }));
    }
    onCreateComponent("collection-list", { dataSourceId: sourceId });
  };

  const ensureSelectedContainerExpanded = () => {
    if (selectedComponent?.type === "box" || selectedComponent?.type === "collection-list") {
      setExpandedComponents((prev) => ({ ...prev, [selectedComponent.id]: true }));
    }
  };

  const isNestableComponent = (component: SkinComponent) =>
    component.type === "box" || component.type === "collection-list" || component.type === "tabs";

  useEffect(() => {
    if (!selectedComponent) return;

    const componentsById = new Map(components.map((component) => [component.id, component]));
    const idsToExpand = new Set<string>();

    const selected = componentsById.get(selectedComponent.id);
    if (selected && isNestableComponent(selected)) {
      idsToExpand.add(selected.id);
    }

    let cursor = selected;
    const visited = new Set<string>();
    while (cursor?.parentId) {
      if (visited.has(cursor.parentId)) break;
      visited.add(cursor.parentId);

      const parent = componentsById.get(cursor.parentId);
      if (!parent) break;
      idsToExpand.add(parent.id);
      cursor = parent;
    }

    if (idsToExpand.size === 0) return;

    setExpandedComponents((prev) => {
      let changed = false;
      const next = { ...prev };
      idsToExpand.forEach((id) => {
        if (next[id] !== true) {
          next[id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [components, selectedComponent]);

  const renderComponent = (component: SkinComponent, level: number = 0) => {
    const IconComponent =
      component.type === "text"
        ? Type
        : component.type === "image"
          ? Image
        : component.type === "icon"
            ? Star
            : component.type === "dropdown"
              ? DropdownIcon
              : component.type === "collection-list"
                ? Database
                : component.type === "tabs"
                  ? LayoutList
                  : Square;
    const children = getChildComponents(component.id);
    const isExpanded = expandedComponents[component.id] ?? false;
    const isEditing = editingComponentId === component.id;

    return (
      <div key={component.id}>
        <>
            {/* biome-ignore lint/a11y/useSemanticElements: complex drag/drop interactions require div */}
            <div
              ref={(element) => {
                if (element) {
                  componentRowRefs.current.set(component.id, element);
                } else {
                  componentRowRefs.current.delete(component.id);
                }
              }}
              data-component-row="true"
              draggable
              onDragStart={(e) => handleDragStart(e, component.id)}
              onDragOver={(e) => handleDragOver(e, component.id)}
              onDrop={(e) => handleDrop(e, component.id)}
              onDragEnd={handleDragEnd}
              className={`group relative flex items-center justify-between p-2 rounded cursor-pointer transition-colors duration-150 ease-out ${
                selectedComponent?.id === component.id
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-700 text-gray-300"
              } ${
                dragOverComponent === component.id && dragOverPosition === "inside"
                  ? "bg-gray-600"
                  : ""
              }`}
              style={{ marginLeft: `${level * 16}px` }}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onComponentSelect(component);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onComponentSelect(component);
                }
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                handleDoubleClick(component);
              }}
            >
              <div
                className={`pointer-events-none absolute -top-1 left-0 right-0 h-0.5 rounded-full bg-blue-400 transition-all duration-150 ease-out ${
                  dragOverComponent === component.id && dragOverPosition === "before"
                    ? "opacity-100 scale-x-100"
                    : "opacity-0 scale-x-95"
                }`}
              />
              <div
                className={`pointer-events-none absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-blue-400 transition-all duration-150 ease-out ${
                  dragOverComponent === component.id && dragOverPosition === "after"
                    ? "opacity-100 scale-x-100"
                    : "opacity-0 scale-x-95"
                }`}
              />
              <div className="flex items-center flex-1 min-w-0">
                {isNestableComponent(component) && children.length > 0 ? (
                  <button
                    type="button"
                    className="mr-1 flex h-4 w-4 items-center justify-center text-gray-300 transition-transform duration-200 ease-out hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedComponents((prev) => ({
                        ...prev,
                        [component.id]: !(prev[component.id] ?? true),
                      }));
                    }}
                    aria-label={isExpanded ? "Collapse component" : "Expand component"}
                  >
                    <ChevronRight
                      size={14}
                      className={`transition-transform duration-200 ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                ) : (
                  <span className="mr-1 h-4 w-4 flex-shrink-0" />
                )}
                {IconComponent && (
                  <IconComponent size={14} className="mr-2 flex-shrink-0 text-white" />
                )}
                {isEditing ? (
                  <Input
                    variant="compact"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleRenameSubmit}
                    onKeyDown={handleRenameKeyDown}
                    className="h-6 px-2 text-xs bg-gray-600 border-gray-500 text-white flex-1 min-w-0 focus:ring-0 focus:border-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-500"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="text-xs truncate flex-1">{component.name}</span>
                )}
              </div>

              <div className="flex items-center space-x-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1 h-auto text-gray-300 hover:text-blue-400 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdateComponent(component.id, { visible: !component.visible });
                  }}
                  title={component.visible ? "Hide component" : "Show component"}
                >
                  {component.visible ? (
                    <Eye size={12} className="text-white" />
                  ) : (
                    <EyeOff size={12} className="text-white" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1 h-auto text-gray-300 hover:text-red-400 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteComponent(component.id);
                  }}
                  title="Delete component"
                >
                  <Trash2 size={12} className="text-white" />
                </Button>
              </div>
            </div>
        </>

        {/* Render nested components */}
        {isExpanded && children.map((child) => renderComponent(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Top Half: Add Skin Component */}
      <div className="h-1/2 flex flex-col min-h-0 border-b border-gray-700">
        {/* biome-ignore lint/a11y/useSemanticElements: complex header with stop propagation */}
        <div
          className="px-4 py-2"
          onClick={(e) => e.stopPropagation()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div className="flex items-center justify-between mb-2 h-7 -mt-0.5 translate-y-[2px]">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
              Craft Skin Component
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-gray-600 text-gray-300 hover:bg-gray-700 cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={(e) => handleTemplateDragStart(e, "box")}
              onDragEnd={handleTemplateDragEnd}
              onClick={(e) => {
                e.stopPropagation();
                ensureSelectedContainerExpanded();
                onCreateComponent("box");
              }}
            >
              <Square size={16} className="mr-2" />
              Box
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-gray-600 text-gray-300 hover:bg-gray-700 cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={(e) => handleTemplateDragStart(e, "collection-list")}
              onDragEnd={handleTemplateDragEnd}
              onClick={(e) => {
                e.stopPropagation();
                setCollectionSourceDialogOpen(true);
              }}
            >
              <Database size={16} className="mr-2" />
              Collection
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-gray-600 text-gray-300 hover:bg-gray-700 cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={(e) => handleTemplateDragStart(e, "text")}
              onDragEnd={handleTemplateDragEnd}
              onClick={(e) => {
                e.stopPropagation();
                ensureSelectedContainerExpanded();
                onCreateComponent("text");
              }}
            >
              <Type size={16} className="mr-2" />
              Text
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-gray-600 text-gray-300 hover:bg-gray-700 cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={(e) => handleTemplateDragStart(e, "image")}
              onDragEnd={handleTemplateDragEnd}
              onClick={(e) => {
                e.stopPropagation();
                ensureSelectedContainerExpanded();
                onCreateComponent("image");
              }}
            >
              <Image size={16} className="mr-2" />
              Image
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-gray-600 text-gray-300 hover:bg-gray-700 cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={(e) => handleTemplateDragStart(e, "icon")}
              onDragEnd={handleTemplateDragEnd}
              onClick={(e) => {
                e.stopPropagation();
                ensureSelectedContainerExpanded();
                onCreateComponent("icon");
              }}
            >
              <Star size={16} className="mr-2" />
              Icon
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-gray-600 text-gray-300 hover:bg-gray-700 cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={(e) => handleTemplateDragStart(e, "dropdown")}
              onDragEnd={handleTemplateDragEnd}
              onClick={(e) => {
                e.stopPropagation();
                ensureSelectedContainerExpanded();
                onCreateComponent("dropdown");
              }}
            >
              <DropdownIcon size={16} className="mr-2" />
              Dropdown
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-gray-600 text-gray-300 hover:bg-gray-700 cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={(e) => handleTemplateDragStart(e, "tabs")}
              onDragEnd={handleTemplateDragEnd}
              onClick={(e) => {
                e.stopPropagation();
                ensureSelectedContainerExpanded();
                onCreateComponent("tabs");
              }}
            >
              <LayoutList size={16} className="mr-2" />
              Tabs
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Half: Component Tree */}
      {/* biome-ignore lint/a11y/useSemanticElements: component container with click handler */}
      <div
        className="h-1/2 flex flex-col min-h-0"
        onClick={handleContainerClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleContainerClick(e as unknown as React.MouseEvent);
          }
        }}
      >
        <ScrollArea className="h-full">
          {/* biome-ignore lint/a11y/useSemanticElements: component tree container with click handler */}
          <div
            className="px-4 py-2 component-tree-container"
            onClick={handleComponentTreeClick}
            onDragOver={handleTreeDragOver}
            onDragLeave={handleTreeDragLeave}
            onDrop={clearDropIndicator}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleComponentTreeClick(e as unknown as React.MouseEvent);
              }
            }}
          >
            {/* biome-ignore lint/a11y/useSemanticElements: interactive div for component tree header */}
            <div
              className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-3"
              onClick={(e) => e.stopPropagation()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              Component Tree
            </div>

            {components.length === 0 ? (
              <>
                {/* biome-ignore lint/a11y/useSemanticElements: interactive div for empty state */}
                <div
                  className="text-xs text-gray-500"
                  onClick={(e) => e.stopPropagation()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  No components added yet
                </div>
              </>
            ) : (
              <div className="space-y-1">
                {rootComponents.map((component) => renderComponent(component))}
              </div>
            )}

          </div>
        </ScrollArea>
      </div>

      <CollectionSourceDialog
        isOpen={collectionSourceDialogOpen}
        onClose={() => setCollectionSourceDialogOpen(false)}
        onSourceSelected={handleCollectionSourceSelected}
      />
    </div>
  );
};
